import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Chip,
  Button,
  CircularProgress,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PhoneIcon from "@mui/icons-material/Phone";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getDeliveryUserOrders, changeOrderStatus } from "../services/OrderStatus/OrderStatusApi";
import { ThemeContext } from "../context/ThemeContextProvider";
import { useAuth } from "../context/AuthContext";
import { useFeatures } from "../context/PermissionsAndFeatures/FeaturesContext";

const DeliveryOrdersPage = () => {
  const { mode } = useContext(ThemeContext);
  const { logout } = useAuth(); // إضافة: دالة تسجيل الخروج من الكونتكست
  // استخلاص ميزة العرض الجوال للتوصيل
  const { hasFeature, featuresLoading } = useFeatures();
  const deliveryMobileViewEnabled = !featuresLoading && hasFeature("delivery_mobile_view");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deliveringIds, setDeliveringIds] = useState([]);
  const [filterStatus, setFilterStatus] = useState("الكل");
  const [lastUpdate, setLastUpdate] = useState(null);
  const subdomain = window.location.hostname.split('.')[0];
  const IMAGE_BASE_URL = `https://${subdomain}.restocash.app/`;
  const placeholderImage = "https://via.placeholder.com/300x140?text=No+Image";

  const fetchOrders = async () => {
    try {
      const res = await getDeliveryUserOrders();
      setOrders(Array.isArray(res) ? res : []);
    } catch (err) {
      setOrders([]);
    }
    setLoading(false);
    setLastUpdate(new Date());
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // أثناء تحميل حالة الميزات: أظهر سبينر بسيط
  if (featuresLoading) {
    return (
      <Box sx={{ textAlign: "center", mt: 6 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>جاري التحقق من صلاحيات العرض...</Typography>
      </Box>
    );
  }

  // لو الميزة غير مفعّلة: اعرض صفحة مختصرة فقط (تحذير + زر تسجيل خروج)
  if (!deliveryMobileViewEnabled) {
    return (
      <Box sx={{
        p: 3,
        maxWidth: "600px",
        mx: "auto",
        bgcolor: mode === "dark" ? "#232334" : "#fff",
        color: mode === "dark" ? "#fff" : "inherit",
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}>
        <Typography variant="h6" color="error" align="center">
          🚫 ميزة العرض الجوال للتوصيل (delivery_mobile_view) غير مفعّلة. لا يمكن عرض الطلبات أو إتمام أي عمليات.
        </Typography>
        <Button
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={() => logout && logout()}
          sx={{ borderRadius: "10px", fontWeight: "bold", px: 2 }}
        >
          تسجيل خروج
        </Button>
      </Box>
    );
  }
 
  // دالة تحويل الوقت إلى نظام 12 ساعة
  const formatTime12 = (timeStr) => {
    if (!timeStr) return "-";
    const [h, m] = timeStr.split(":");
    let hour = Number(h);
    const ampm = hour >= 12 ? "م" : "ص";
    hour = hour % 12 || 12;
    return `${hour}:${m} ${ampm}`;
  };

  const handleStartDelivery = async (orderId, status = "underway") => {
    // منع العمليات إذا كانت الميزة معطّلة
    if (!deliveryMobileViewEnabled) {
      alert("🚫 ميزة العرض الجوال للتوصيل غير مفعّلة. لا يمكن إتمام العمليات.");
      return;
    }
    try {
      await changeOrderStatus(orderId, status);
      setDeliveringIds((prev) => [...prev, orderId]);
      // تحديث بيانات الصفحة بعد نجاح الإرسال
      const res = await getDeliveryUserOrders();
      setOrders(Array.isArray(res) ? res : []);
    } catch (err) {
      alert("حدث خطأ أثناء بدء التوصيل");
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 5, bgcolor: mode === "dark" ? "#232334" : "#fff", color: mode === "dark" ? "#fff" : "inherit" }}>
        <Typography variant="h6" color={mode === "dark" ? "#fff" : "text.secondary"}>
          جاري تحميل الطلبات...
        </Typography>
      </Box>
    );
  }

  if (!orders.length) {
    return (
      <Box sx={{ textAlign: "center", mt: 5, bgcolor: mode === "dark" ? "#232334" : "#fff", color: mode === "dark" ? "#fff" : "inherit" }}>
        <Typography variant="h6" color={mode === "dark" ? "#fff" : "text.secondary"}>
          لا توجد طلبات توصيل حالياً 🚴‍♂️
        </Typography>
      </Box>
    );
  }

  // الحالات المتاحة للفلترة
  const statusTabs = [
    { key: "الكل", label: "الكل" },
    { key: "underway", label: "جاري التوصيل" },
    { key: "out_of_preparation", label: "تم الانتهاء" },
    { key: "delivered", label: "تم التسليم" },
  ];

  // فقط الحالات المطلوبة
  const allowedStatuses = ["underway", "out_of_preparation", "delivered"];
  const filteredOrders = orders
    .filter(order => allowedStatuses.includes(order.status))
    .filter(order => filterStatus === "الكل" || order.status === filterStatus);

  // حساب عدد الحالات لكل فلتر
  const getTabCount = (key) => {
    if (key === "الكل") return orders.filter(order => allowedStatuses.includes(order.status)).length;
    return orders.filter(order => order.status === key).length;
  };

  return (
    <Box sx={{
      p: 2,
      maxWidth: "600px",
      mx: "auto",
      bgcolor: mode === "dark" ? "#232334" : "#fff",
      color: mode === "dark" ? "#fff" : "inherit",
      minHeight: "100vh"
    }}>
      {/* تحذير إذا كانت الميزة معطّلة (الصفحة مرئية لكن العمليات معطلة) */}
      {!deliveryMobileViewEnabled && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" color="error" align="center">
            🚫 ميزة العرض الجوال للتوصيل (delivery_mobile_view) غير مفعّلة. الصفحة للعرض فقط — لا يمكن إتمام أي عمليات.
          </Typography>
        </Box>
      )}
      {/* زر تحديث البيانات وأخر موعد تحديث */}
      <Box sx={{
        mb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="outlined"
            color="info"
            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              setLoading(true);
              fetchOrders();
            }}
            sx={{
              borderRadius: "10px",
              fontWeight: "bold",
              px: 1.5,
              py: 0.7,
              bgcolor: "#fff",
              color: "#1976d2",
              fontSize: "15px",
              minWidth: "80px",
              boxShadow: "0 2px 8px #1976d244",
              "&:hover": { bgcolor: "#e3f2fd" }
            }}
          >
            تحديث
          </Button>
          {lastUpdate && (
            <Typography variant="caption" sx={{
              color: "#90caf9",
              fontWeight: "bold",
              fontSize: "13px",
              ml: 1
            }}>
              آخر تحديث: {lastUpdate.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
        {/* زر تسجيل خروج */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={() => logout && logout()}
            sx={{
              borderRadius: "10px",
              fontWeight: "bold",
              px: 1.5,
              py: 0.7,
              minWidth: "120px",
            }}
          >
            تسجيل خروج
          </Button>
        </Box>
      </Box>

      {/* فلتر الحالات */}
      <Box sx={{
        mb: 3,
        display: "flex",
        gap: 1.5,
        justifyContent: "center",
        flexWrap: "wrap"
      }}>
        {statusTabs.map(tab => (
          <Box
            key={tab.key}
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: filterStatus === tab.key ? "#FFC107" : "transparent",
              border: filterStatus === tab.key ? "2px solid #FFC107" : "1.5px solid #6c6c7a",
              color: filterStatus === tab.key ? "#232334" : "#fff",
              borderRadius: "14px",
              px: 2,
              py: 0.7,
              minWidth: "90px",
              boxShadow: filterStatus === tab.key ? "0 2px 8px #FFC10744" : undefined,
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "15px",
              transition: "all 0.18s",
              "&:hover": {
                bgcolor: "#FFD54F",
                color: "#232334",
                border: "2px solid #FFD54F"
              }
            }}
            onClick={() => setFilterStatus(tab.key)}
          >
            <Box sx={{
              bgcolor: "#fff",
              color: "#232334",
              borderRadius: "8px",
              fontWeight: "bold",
              fontSize: "13px",
              minWidth: "28px",
              px: 1.2,
              py: 0.2,
              mr: 1,
              textAlign: "center",
              boxShadow: "0 1px 4px #23233422"
            }}>
              {getTabCount(tab.key)}
            </Box>
            <span style={{
              flex: 1,
              textAlign: "center",
              fontWeight: "bold"
            }}>
              {tab.label}
            </span>
          </Box>
        ))}
      </Box>
      {filteredOrders.map((order) => (
        <Card
          key={order.id}
          sx={{
            mb: 3,
            borderRadius: 3,
            boxShadow: mode === "dark" ? "0 4px 12px #0008" : "0 4px 12px rgba(0,0,0,0.1)",
            bgcolor: mode === "dark" ? "#2c2c3a" : "#fff",
            color: mode === "dark" ? "#fff" : "inherit",
          }}
        >
          <CardContent>
            {/* رأس الطلب */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="h6" sx={{ color: mode === "dark" ? "#fff" : "inherit" }}>
                طلب رقم: #{order.id}
              </Typography>
              <Chip
                label={
                  order.status === "pending"
                    ? "قيد التنفيذ"
                    : order.status === "out_of_preparation"
                    ? "تم الانتهاء"
                    : order.status === "delivered"
                    ? "تم التسليم"
                    : order.status === "underway"
                    ? "جاري التوصيل"
                    : order.status
                }
                color={
                  order.status === "pending"
                    ? "warning"
                    : order.status === "out_of_preparation"
                    ? "warning"
                    : order.status === "delivered"
                    ? "success"
                    : order.status === "underway"
                    ? "error"
                    : "success"
                }
                size="small"
                sx={{
                  bgcolor:
                    order.status === "pending"
                      ? (mode === "dark" ? "#FFC107" : undefined)
                      : order.status === "out_of_preparation"
                      ? "#FFC107"
                      : order.status === "delivered"
                      ? "#43a047"
                      : order.status === "underway"
                      ? "#d32f2f"
                      : (mode === "dark" ? "#4CAF50" : undefined),
                  color:
                    order.status === "out_of_preparation"
                      ? "#232334"
                      : order.status === "delivered"
                      ? "#fff"
                      : order.status === "underway"
                      ? "#fff"
                      : mode === "dark"
                      ? "#232334"
                      : undefined,
                  fontWeight: "bold"
                }}
              />
            </Box>

            {/* تاريخ الطلب */}
            <Typography variant="body2" sx={{ color: mode === "dark" ? "#90caf9" : "inherit", fontWeight: "bold", mb: 0.5 }}>
              تاريخ الطلب
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: mode === "dark" ? "#b0bec5" : "text.secondary" }}>
              <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, color: mode === "dark" ? "#b0bec5" : "inherit" }} />
              {order.date} - {formatTime12(order.open_time)}
            </Typography>

            <Typography variant="body2" sx={{ mb: 1, color: mode === "dark" ? "#90caf9" : "inherit" }}>
              الفرع: {order.root_name}
            </Typography>

            {/* بيانات العميل */}
            <Divider sx={{ my: 1, bgcolor: mode === "dark" ? "#444" : undefined }} />
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: mode === "dark" ? "#90caf9" : "inherit" }}>
              بيانات العميل
            </Typography>
            <Typography variant="body2" sx={{ color: mode === "dark" ? "#fff" : "inherit" }}>
              الاسم: {order.customer?.name || "-"}
            </Typography>
            <Typography variant="body2" sx={{ color: mode === "dark" ? "#fff" : "inherit" }}>
              <PhoneIcon sx={{ fontSize: 16, mr: 0.5, color: mode === "dark" ? "#90caf9" : "inherit" }} />
              {order.customer?.phone1 || "لا يوجد"}
            </Typography>
            <Typography variant="body2" sx={{ color: mode === "dark" ? "#fff" : "inherit" }}>
              العنوان: {order.customer?.address1 || "غير محدد"}
            </Typography>

            {/* المنتجات */}
            <Divider sx={{ my: 1, bgcolor: mode === "dark" ? "#444" : undefined }} />
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: mode === "dark" ? "#90caf9" : "inherit" }}>
              المنتجات
            </Typography>
            {order.items.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  my: 1,
                  p: 1,
                  borderRadius: 2,
                  bgcolor: mode === "dark" ? "#232334" : "#fafafa",
                  color: mode === "dark" ? "#fff" : "inherit",
                }}
              >
                <CardMedia
                  component="img"
                  image={item.product_image ? `${IMAGE_BASE_URL}${item.product_image}` : placeholderImage}
                  alt={item.product_name}
                  sx={{
                    width: 60,
                    height: 60,
                    objectFit: "cover",
                    borderRadius: "12px",
                    mx: 1,
                    mb: 0,
                    border: mode === "dark" ? "2px solid #444" : "2px solid #fffbe6",
                    boxShadow: mode === "dark" ? "0 2px 8px #222" : "0 2px 8px #ffb30022"
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" sx={{ color: mode === "dark" ? "#fff" : "inherit" }}>
                    {item.product_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: mode === "dark" ? "#b0bec5" : "text.secondary" }}>
                    الكمية: {item.quantity} × السعر: {item.price} ={" "}
                    <b>{item.total} ج.م</b>
                  </Typography>
                  {item.note && (
                    <Typography
                      variant="caption"
                      sx={{ color: "orange", display: "block" }}
                    >
                      ملاحظة: {item.note}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}

            {/* الإجمالي */}
            <Divider sx={{ my: 1, bgcolor: mode === "dark" ? "#444" : undefined }} />
            <Typography variant="h6" sx={{ textAlign: "right", color: mode === "dark" ? "#90caf9" : "inherit" }}>
              الإجمالي: {order.total} ج.م
            </Typography>

            {/* زر التوصيل */}
            <Box sx={{ mt: 2, textAlign: "center" }}>
              {order.status === "underway" ? (
                <Button
                  variant="contained"
                  color="error"
                  sx={{
                    borderRadius: "20px",
                    px: 4,
                    fontWeight: "bold",
                    bgcolor: "#d32f2f",
                    color: "#fff",
                  }}
                  onClick={() => handleStartDelivery(order.id, "delivered")}
                  disabled={deliveringIds.includes(order.id) || !deliveryMobileViewEnabled}
                >
                  تسليم
                </Button>
              ) : order.status === "delivered" ? (
                <Button
                  variant="contained"
                  color="success"
                  sx={{
                    borderRadius: "20px",
                    px: 4,
                    fontWeight: "bold",
                    bgcolor: "#43a047",
                    color: "#fff",
                  }}
                  disabled
                >
                  تم التسلم
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DeliveryDiningIcon />}
                  sx={{
                    borderRadius: "20px",
                    px: 4,
                    bgcolor: mode === "dark" ? "#1976d2" : undefined,
                    color: mode === "dark" ? "#fff" : undefined,
                    fontWeight: "bold",
                    boxShadow: mode === "dark" ? "0 2px 8px #1976d244" : undefined,
                    "&:hover": { bgcolor: mode === "dark" ? "#1565c0" : undefined }
                  }}
                  onClick={() => handleStartDelivery(order.id)}
                  disabled={deliveringIds.includes(order.id) || order.status === "underway" || !deliveryMobileViewEnabled}
                >
                  بدء التوصيل
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default DeliveryOrdersPage;
